import { motion } from "framer-motion";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import {
  TbArrowRight,
  TbDotsVertical,
  TbSeparatorHorizontal,
} from "react-icons/tb";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import { SpinnerCircular } from "spinners-react";
import styled, { useTheme } from "styled-components";
import bookApi from "../../app-api/books";
import { memoriesApi } from "../../app-api/memories";
import { selectUser, userActions } from "../../app/reducers/UserSlice";
import Button from "../../components/Button";
import toast from "../../components/Toast";
import { Footnote, Title2 } from "../../styles/typography";
import { EVENTS, track } from "../../util/analytics";
import Navbar from "../book/components/Navbar";
import { MOBILE_BREAKPOINT } from "./components/ChapterDatePills";
import DeleteChapterDialog from "./components/DeleteChapterDialog";
import PartialSelectionDialog from "./components/PartialSelectionDialog";
import ReorderToggle from "./components/ReorderToggle";
import ReorderableChapterList from "./components/ReorderableChapterList";

const IS_MOBILE_BREAKPOINT = 500;
const MemoriesContainer = styled.div`
  margin: 0 auto;
  margin-top: 98px;
  padding: 24px;
  background: rgb(${(props) => props.theme.colors.Bgprimary});
  min-height: calc(100vh - 114px);
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 1000px;
  width: 100%;
  svg {
    min-height: 24px;
    min-width: 24px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: space-between;
  button {
    transition: all 150ms ease-in-out;
  }
  @media screen and (max-width: ${IS_MOBILE_BREAKPOINT}px) {
    button {
      width: 50px;
      height: 50px;
    }
  }
`;

const ControlsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const ControlsText = styled(Footnote)`
  color: rgb(${(props) => props.theme.colors.neutral400});
  b {
    color: rgb(${(props) => props.theme.colors.neutral600});
  }
  && svg {
    min-width: 0;
    min-height: 0;
    width: 1.1em;
    height: 1.1em;
    vertical-align: -0.2em;
  }
`;

const ReorderHint = styled(Footnote)`
  color: rgb(${(props) => props.theme.colors.neutral400});
  font-style: italic;
  line-height: 1.5;
  && svg {
    min-width: 0;
    min-height: 0;
    width: 1.1em;
    height: 1.1em;
    vertical-align: -0.2em;
  }
`;

const ScrollableTableContainer = styled(motion.div)`
  max-height: calc(84vh - (98px + 7rem));
  overflow: auto;
  border: 2px solid rgb(${(props) => props.theme.colors.neutral150});
  border-radius: 8px;
  padding: 8px;
  padding-top: 0;
`;

const MemoriesList = () => {
  const theme = useTheme();
  const [memories, setMemories] = useState([]);
  const user = useSelector(selectUser);
  const [selectedMemories, setSelectedMemories] = useState([]);
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [totalMemories, setTotalMemories] = useState();
  const [reorderMode, setReorderMode] = useState(false);
  const [showPartialConfirm, setShowPartialConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const memoriesRef = useRef([]);
  const bookRef = useRef(null);
  const bookCreationRef = useRef(null);
  const lastPersistedOrderRef = useRef([]);
  const lastMemoriesRef = useRef([]);
  const isMobileReorder = useMediaQuery({ maxWidth: MOBILE_BREAKPOINT });
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(userActions.principal());
    const abortController = new AbortController();
    bookApi
      .getBook(abortController.signal)
      .then(async (bookRes) => {
        const currentBook = bookRes.book;
        const memoriesRes = await bookApi.getBookMemories(
          currentBook?.id,
          abortController.signal,
        );
        const { memories, allMemoryIds, bookMemoryIds } = memoriesRes;
        const remaining = allMemoryIds.filter(
          (id) => !bookMemoryIds.includes(id),
        );
        const ordered = [...bookMemoryIds, ...remaining]
          .map((id) => memories[id])
          .filter(Boolean);

        setMemories(ordered);
        memoriesRef.current = ordered;
        lastMemoriesRef.current = ordered;
        // No book yet (or no memories saved to it) means everything starts checked.
        setSelectedMemories(
          bookMemoryIds.length === 0
            ? ordered.map((memory) => memory.memoryID)
            : bookMemoryIds,
        );
        setBook(currentBook);
        bookRef.current = currentBook;
        lastPersistedOrderRef.current = bookMemoryIds;
        setTotalMemories(ordered.length);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") {
          return;
        }
        console.log("error fetching book memories", err);
        setLoading(false);
      });

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const proceedToOverview = () => {
    navigate("/book/overview", {
      state: {
        selectedMemories,
        selectedMemoriesDetails: memories
          .filter((m) => selectedMemories.includes(m.memoryID))
          .map((m) => ({
            memoryID: m.memoryID,
            memoryTitle: m.memoryTitle,
            date: m.date,
          })),
        bookDetails: book ?? undefined,
      },
    });
  };

  const handleBookSubmission = () => {
    track(EVENTS.BOOK_SUBMIT_STARTED, {
      selectedCount: selectedMemories.length,
      totalCount: memories.length,
    });
    if (selectedMemories.length < memories.length) {
      setShowPartialConfirm(true);
      return;
    }
    proceedToOverview();
  };

  const handleToggleSelect = (memoryID) => {
    setSelectedMemories((prev) =>
      prev.includes(memoryID)
        ? prev.filter((_) => _ !== memoryID)
        : prev.concat([memoryID]),
    );
  };

  const handleSelectAll = () => {
    const allChecked = memories.every((memory) =>
      selectedMemories.includes(memory.memoryID),
    );
    if (allChecked) {
      setSelectedMemories([]);
    } else {
      setSelectedMemories(memories.map((ele) => ele.memoryID));
    }
    track(EVENTS.MEMORIES_SELECT_ALL_TOGGLED, {
      selected: !allChecked,
      memoryCount: memories.length,
    });
  };

  const handleReorder = (nextMemories) => {
    memoriesRef.current = nextMemories;
    setMemories(nextMemories);
  };

  /**
   *
   * @param {string[]} orderedMemories memory IDs ordered based on user preference
   */
  const ensureBook = async (orderedMemories) => {
    if (bookRef.current?.id) {
      return bookRef.current.id;
    }
    if (!bookCreationRef.current) {
      const bookTitle = `A book by ${user?.name?.first} ${user?.name?.last}`;
      bookCreationRef.current = bookApi
        .createABook({
          memoryID: orderedMemories,
          title: bookTitle,
          firstName: user?.name?.first ?? null,
          lastName: user?.name?.last ?? null,
        })
        .then((response) => {
          bookRef.current = response;
          setBook(response);
          return response?.id;
        })
        .finally(() => {
          bookCreationRef.current = null;
        });
    }
    return bookCreationRef.current;
  };

  const handleDragEnd = () => {
    const orderedSelection = memoriesRef.current
      .filter((memory) => selectedMemories.includes(memory.memoryID))
      .map((memory) => memory.memoryID);

    // Dragging an unselected chapter changes the on-screen list but not the
    // selected set's order, so there's nothing to persist. Snap the list
    // back to the last saved order rather than leaving a position on screen
    // that silently reverts on the next load.
    const unchanged =
      orderedSelection.length === lastPersistedOrderRef.current.length &&
      orderedSelection.every(
        (id, idx) => id === lastPersistedOrderRef.current[idx],
      );
    if (orderedSelection.length === 0 || unchanged) {
      memoriesRef.current = lastMemoriesRef.current;
      setMemories(lastMemoriesRef.current);
      toast({
        message:
          "Select this chapter to include it in your book - its position will be saved once selected.",
        type: "info",
        toastId: "chapter-order-unselected",
      });
      return;
    }

    const persistOrder = async () => {
      const bookID = await ensureBook(orderedSelection);
      await bookApi.updateBookMemories(bookID, { memoryID: orderedSelection });
      lastPersistedOrderRef.current = orderedSelection;
      lastMemoriesRef.current = memoriesRef.current;
      track(EVENTS.CHAPTERS_REORDERED, {
        chapterCount: orderedSelection.length,
      });
    };

    toast({
      message: "Chapter order saved.",
      promise: {
        function: persistOrder(),
        pending: { message: "Saving chapter order..." },
        error: { message: "Unable to save the new chapter order." },
      },
    });
  };

  const handleDeleteRequest = (memoryID) => {
    const target = memoriesRef.current.find(
      (memory) => memory.memoryID === memoryID,
    );
    if (target) {
      setDeleteTarget({
        ...target,
        inBook: selectedMemories.includes(memoryID),
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    const memoryID = deleteTarget.memoryID;
    const wasInBook = deleteTarget.inBook;
    setDeleting(true);

    const deleteChapter = async () => {
      await memoriesApi.deleteMemory(memoryID);
      track(EVENTS.CHAPTER_DELETED, { wasInBook });

      const nextMemories = memoriesRef.current.filter(
        (memory) => memory.memoryID !== memoryID,
      );
      memoriesRef.current = nextMemories;
      lastMemoriesRef.current = nextMemories;
      setMemories(nextMemories);
      setSelectedMemories((prev) => prev.filter((id) => id !== memoryID));
      setTotalMemories(nextMemories.length);

      if (
        bookRef.current?.id &&
        lastPersistedOrderRef.current.includes(memoryID)
      ) {
        const nextOrder = lastPersistedOrderRef.current.filter(
          (id) => id !== memoryID,
        );
        await bookApi.updateBookMemories(bookRef.current.id, {
          memoryID: nextOrder,
        });
        lastPersistedOrderRef.current = nextOrder;
      }
    };

    const deletion = deleteChapter();

    toast({
      message: wasInBook
        ? "Chapter deleted and removed from your book."
        : "Chapter deleted.",
      promise: {
        function: deletion,
        pending: { message: "Deleting chapter..." },
        error: { message: "Unable to delete chapter." },
      },
    });

    try {
      await deletion;
    } catch (err) {
      console.log("error deleting chapter", err);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <MemoriesContainer>
          <SpinnerCircular
            size={48}
            thickness={200}
            speed={100}
            color={`rgba(${theme.colors.neutral200}, 1)`}
            secondaryColor={`rgba(${theme.colors.neutral100}, 1)`}
            style={{
              justifySelf: "center",
              position: "relative",
              left: "45%",
            }}
          />
        </MemoriesContainer>
      </>
    );
  }

  const allChecked = memories.every((memory) =>
    selectedMemories.includes(memory.memoryID),
  );
  // Mobile keeps the exclusive reorder mode (checkboxes and Submit make the
  // small layout too crowded); on larger screens selection stays available
  // while reordering.
  const exclusiveReorder = reorderMode && isMobileReorder;
  return (
    <>
      <Navbar />
      <MemoriesContainer>
        <TitleRow $reorderEnabled={exclusiveReorder}>
          <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <Title2 as="h2">
              {user?.name?.first} {user?.name?.last}'s chapters{" "}
              {totalMemories ? <>({totalMemories})</> : null}
            </Title2>
          </div>
          {memories.length !== 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: "4px",
              }}
            >
              <Button
                variant="primary"
                onButtonClick={handleBookSubmission}
                disabled={selectedMemories.length === 0 || reorderMode}
              >
                Next <TbArrowRight />
              </Button>
            </div>
          )}
        </TitleRow>

        {memories.length !== 0 && (
          <ControlsBar>
            <ReorderToggle checked={reorderMode} onChange={setReorderMode} />
            <ControlsText>
              <b>{selectedMemories.length}</b> of {memories.length} selected for
              this book
            </ControlsText>
          </ControlsBar>
        )}

        {memories.length !== 0 && reorderMode && (
          <ReorderHint>
            Drag the <TbSeparatorHorizontal /> handle to reorder, or tap{" "}
            <TbDotsVertical /> for quick moves.
          </ReorderHint>
        )}

        <ScrollableTableContainer id="scrollableMemories" layoutScroll>
          <ReorderableChapterList
            memories={memories}
            reorderMode={reorderMode}
            selectedMemories={selectedMemories}
            allChecked={allChecked}
            onToggleSelect={handleToggleSelect}
            onSelectAll={handleSelectAll}
            onReorder={handleReorder}
            onDragEnd={handleDragEnd}
            onDeleteRequest={handleDeleteRequest}
          />
        </ScrollableTableContainer>
      </MemoriesContainer>

      <PartialSelectionDialog
        open={showPartialConfirm}
        onOpenChange={setShowPartialConfirm}
        checkedCount={selectedMemories.length}
        totalCount={memories.length}
        onContinue={() => {
          track(EVENTS.PARTIAL_SELECTION_CONFIRMED, {
            selectedCount: selectedMemories.length,
            totalCount: memories.length,
          });
          setShowPartialConfirm(false);
          proceedToOverview();
        }}
        onGoBack={() => setShowPartialConfirm(false)}
      />

      <DeleteChapterDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        chapterTitle={deleteTarget?.memoryTitle}
        inBook={deleteTarget?.inBook}
        deleting={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default MemoriesList;
